export interface Mahadasha {
  number: number;
  startAge: number;
  endAge: number;
}

export interface PratyantarDasha {
  number: number;
  sequence?: number;
  startDate: string;
  endDate: string;
  durationDays: number;
}

export interface Antardasha {
  number: number;
  sequence: number;
  startDate: string;
  endDate: string;
  durationDays: number;
}

export interface VedicPlanetData {
  name: string;
  quality: string;
  remedy: string;
}

export interface MissingNumber extends VedicPlanetData {
  num: number;
}

export interface ActivePlane {
  name: string;
  numbers: number[];
  desc: string;
}

export interface VedicYoga {
  id: string;
  name: string;
  numbers: number[];
  type: string;
  effect: string;
}

export interface VedicNumerologyResult {
  dob: string;
  ruling: number;
  destiny: number;
  grid: Record<number, number>;
  mahadashas: Mahadasha[];
  antardasha: number;
  currentAntardasha: number;
  antardashas: Antardasha[];
  pratyantarDashas: PratyantarDasha[];
  missing: MissingNumber[];
  activePlanes: ActivePlane[];
  yogas?: VedicYoga[];
  meta: {
    targetYear: number;
    birthdayThisYear: string;
    yearSum: number;
    weekdayNum: number;
    gridLayout: number[][];
  };
}

const weekdayMap: Record<number, number> = {
  0: 1,
  1: 2,
  2: 9,
  3: 5,
  4: 3,
  5: 6,
  6: 8,
};

const pratyantarDurations: Record<number, number> = {
  1: 8,
  2: 16,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  7: 56,
  8: 64,
  9: 72,
};

export const vedicData: {
  planets: Record<number, VedicPlanetData>;
  planes: ActivePlane[];
} = {
  planets: {
    1: { name: 'Sun', quality: 'Leadership, Ego, Soul', remedy: 'Offer water to the Sun; respect father.' },
    2: { name: 'Moon', quality: 'Mind, Emotions, Peace', remedy: 'Drink water in silver glass; respect mother.' },
    3: { name: 'Jupiter', quality: 'Wisdom, Wealth, Children', remedy: 'Apply Saffron (Kesar) tilak; respect teachers.' },
    4: { name: 'Rahu', quality: 'Innovation, Sudden Gains', remedy: 'Feed birds; help the physically challenged.' },
    5: { name: 'Mercury', quality: 'Business, Speech, Logic', remedy: 'Feed green fodder to cows; keep a green handkerchief.' },
    6: { name: 'Venus', quality: 'Luxury, Love, Arts', remedy: 'Use perfumes; help women in need.' },
    7: { name: 'Ketu', quality: 'Intuition, Spirituality', remedy: 'Feed stray dogs; keep a black and white blanket.' },
    8: { name: 'Saturn', quality: 'Karma, Hard Work, Justice', remedy: 'Help laborers; donate black sesame seeds on Saturdays.' },
    9: { name: 'Mars', quality: 'Energy, Courage, Brothers', remedy: 'Recite Hanuman Chalisa; help younger brothers.' },
  },
  planes: [
    { name: 'Mental Plane (3-1-9)', numbers: [3, 1, 9], desc: 'Superior planning and logical thinking.' },
    { name: 'Emotional Plane (6-7-5)', numbers: [6, 7, 5], desc: 'Great communication and emotional intelligence.' },
    { name: 'Practical Plane (2-8-4)', numbers: [2, 8, 4], desc: 'Grounding and ability to execute hard work.' },
  ],
};

const vedicYogaDefinitions: VedicYoga[] = [
  { id: 'fame-success', name: 'Fame Success Yoga', numbers: [3, 1, 9], type: 'Vedic Grid Yoga', effect: 'Fame, success, wealth and spiritual path.' },
  { id: 'kalsarp-374', name: 'Kalsarp Yoga', numbers: [3, 7, 4], type: 'Vedic Grid Yoga', effect: 'High success and stronger cash-flow support.' },
  { id: 'courage', name: 'Courage Yoga', numbers: [9, 7, 2], type: 'Vedic Grid Yoga', effect: 'Self-confidence, courage and heroic attitude.' },
  { id: 'education', name: 'Education Yoga', numbers: [3, 6, 2], type: 'Vedic Grid Yoga', effect: 'Good teacher, intelligence and strong learning ability.' },
  { id: 'spiritual-social', name: 'Spiritual Social Success Yoga', numbers: [1, 7, 8], type: 'Vedic Grid Yoga', effect: 'Multiple income sources, sixth sense and social/spiritual inclination.' },
  { id: 'hardwork', name: 'Hardwork Success Yoga', numbers: [2, 8, 4], type: 'Vedic Grid Yoga', effect: 'Success after hard work; watch upcoming issues and pressure patterns.' },
  { id: 'business', name: 'Business Success Yoga', numbers: [6, 7, 5], type: 'Vedic Grid Yoga', effect: 'Creative business skill, money management and relationship intensity.' },
  { id: 'bandhan-954', name: 'Bandhan Yoga', numbers: [9, 5, 4], type: 'Vedic Grid Yoga', effect: 'Goal achievement and fast decisions; can show binding disputes or pressure.' },
  { id: 'trine-398', name: 'Trine Yoga', numbers: [3, 9, 8], type: 'Trine Yoga', effect: 'Average education/family life; success after sustained hard work.' },
  { id: 'trine-694', name: 'Material Willpower Yoga', numbers: [6, 9, 4], type: 'Trine Yoga', effect: 'Strong will for material life; relationship caution.' },
  { id: 'trine-124', name: 'Education Disturbance Yoga', numbers: [1, 2, 4], type: 'Trine Yoga', effect: 'Education disturbance and ups and downs in life.' },
  { id: 'lshape-917', name: 'Strong Will Yoga', numbers: [9, 1, 7], type: 'L Shape Combination', effect: 'Strong will power and aggressive nature.' },
  { id: 'lshape-136', name: 'Wisdom Teacher Yoga', numbers: [1, 3, 6], type: 'L Shape Combination', effect: 'Intellect, knowledge, wisdom and teaching ability.' },
  { id: 'lshape-754', name: 'Kalsarp Communication Yoga', numbers: [7, 5, 4], type: 'L Shape Combination', effect: 'Hard work with weaker inter-communication skills.' },
  { id: 'pair-67', name: 'Luxury Detachment Combination', numbers: [6, 7], type: 'Two Number Combination', effect: 'Disinterest or reduced attachment toward luxury and love.' },
  { id: 'pair-95', name: 'Action Orientation Combination', numbers: [9, 5], type: 'Two Number Combination', effect: 'Motivated, action oriented, thinks before acting.' },
  { id: 'pair-84', name: 'Determination Combination', numbers: [8, 4], type: 'Two Number Combination', effect: 'Determination and hard work; personal life may feel problematic.' },
  { id: 'pair-78', name: 'Spiritual Deeds Combination', numbers: [7, 8], type: 'Two Number Combination', effect: 'Spirituality and good deeds.' },
  { id: 'pair-75', name: 'Argumentative Law Combination', numbers: [7, 5], type: 'Two Number Combination', effect: 'Argumentative nature; useful for law/analysis, personal life caution.' },
  { id: 'pair-19', name: 'Self Confidence Combination', numbers: [1, 9], type: 'Two Number Combination', effect: 'Leadership with courage and confidence.' },
  { id: 'pair-62', name: 'Art Literature Combination', numbers: [6, 2], type: 'Two Number Combination', effect: 'Art, literature, nature and creative sensitivity.' },
  { id: 'pair-54', name: 'Communication Balance Combination', numbers: [5, 4], type: 'Two Number Combination', effect: 'Good communication with logical mind and balance.' },
  { id: 'pair-36', name: 'Knowledge Cashflow Combination', numbers: [3, 6], type: 'Two Number Combination', effect: 'Knowledge, cash flow and teaching potential.' },
  { id: 'pair-28', name: 'Confusion Combination', numbers: [2, 8], type: 'Two Number Combination', effect: 'Mood swings and work delays.' },
  { id: 'pair-17', name: 'Pure Heart Combination', numbers: [1, 7], type: 'Two Number Combination', effect: 'Noble heart and pure-hearted intention.' },
  { id: 'aspect-24', name: 'Moon Rahu Aspect', numbers: [2, 4], type: 'Drishti Sambandh', effect: 'Problematic nature, mood swings, family tension and hard work.' },
  { id: 'aspect-65', name: 'Venus Mercury Aspect', numbers: [6, 5], type: 'Drishti Sambandh', effect: 'Passion for material things, luxury and travel.' },
  { id: 'aspect-94', name: 'Mars Rahu Aspect', numbers: [9, 4], type: 'Drishti Sambandh', effect: 'Strong will power and dominating tendency.' },
  { id: 'aspect-18', name: 'Sun Saturn Aspect', numbers: [1, 8], type: 'Drishti Sambandh', effect: 'Egoless personality with financial ups and downs.' },
  { id: 'aspect-32', name: 'Jupiter Moon Aspect', numbers: [3, 2], type: 'Drishti Sambandh', effect: 'Philosophical nature and intelligence.' },
  { id: 'aspect-39', name: 'Jupiter Mars Aspect', numbers: [3, 9], type: 'Drishti Sambandh', effect: 'Humanitarian, multitalented and analytical.' },
  { id: 'retro-12', name: 'Sun Moon Retro Aspect', numbers: [1, 2], type: 'Retro Drishti', effect: 'Mood swings, family tension and sensitivity.' },
  { id: 'retro-98', name: 'Mars Saturn Retro Aspect', numbers: [9, 8], type: 'Retro Drishti', effect: 'Humanitarian, hard working; credit may be taken by others.' },
  { id: 'retro-69', name: 'Venus Mars Retro Aspect', numbers: [6, 9], type: 'Retro Drishti', effect: 'Multiple relationship tendency and delayed marriage indications.' },
  { id: 'retro-38', name: 'Jupiter Saturn Retro Aspect', numbers: [3, 8], type: 'Retro Drishti', effect: 'Counselling/consulting talent, occult interest, success after hard work.' },
  { id: 'retro-14', name: 'Sun Rahu Retro Aspect', numbers: [1, 4], type: 'Retro Drishti', effect: 'Family tension, career ups and downs, wasteful expenses.' },
  { id: 'retro-25', name: 'Moon Mercury Retro Aspect', numbers: [2, 5], type: 'Retro Drishti', effect: 'Emotional, spiritual interest and dependency patterns.' },
];

const nextNumber = (num: number) => (num === 9 ? 1 : num + 1);

const parseDob = (dobString: string) => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobString)) {
    const [day, month, year] = dobString.split('/').map(Number);
    const dob = new Date(year, month - 1, day);
    if (
      dob.getFullYear() !== year ||
      dob.getMonth() !== month - 1 ||
      dob.getDate() !== day
    ) {
      throw new Error('DOB is not a valid calendar date.');
    }
    return { dob, year, month, day };
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dobString)) {
    throw new Error('DOB must be in DD/MM/YYYY format.');
  }

  const [year, month, day] = dobString.split('-').map(Number);
  const dob = new Date(year, month - 1, day);

  if (
    dob.getFullYear() !== year ||
    dob.getMonth() !== month - 1 ||
    dob.getDate() !== day
  ) {
    throw new Error('DOB is not a valid calendar date.');
  }

  return { dob, year, month, day };
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const reduceToSingleDigit = (num: number | string) => {
  if (num === 0 || num === '0') return 0;

  let sum = Math.abs(Number(num))
    .toString()
    .split('')
    .filter((digit) => /\d/.test(digit))
    .reduce((total, digit) => total + parseInt(digit, 10), 0);

  while (sum > 9) {
    sum = sum
      .toString()
      .split('')
      .reduce((total, digit) => total + parseInt(digit, 10), 0);
  }

  return sum;
};

export const getAnalysis = (grid: Record<number, number>) => {
  const missing: MissingNumber[] = [];
  const activePlanes: ActivePlane[] = [];
  const yogas: VedicYoga[] = [];

  for (let i = 1; i <= 9; i += 1) {
    if (!grid[i] || grid[i] === 0) {
      missing.push({ num: i, ...vedicData.planets[i] });
    }
  }

  vedicData.planes.forEach((plane) => {
    if (plane.numbers.every((num) => grid[num] > 0)) {
      activePlanes.push(plane);
    }
  });

  vedicYogaDefinitions.forEach((definition) => {
    if (definition.numbers.every((num) => grid[num] > 0)) {
      yogas.push(definition);
    }
  });

  return { missing, activePlanes, yogas };
};

const getAntardashaNumberForYear = (day: number, month: number, year: number) => {
  const birthday = new Date(year, month - 1, day);
  const weekdayNum = weekdayMap[birthday.getDay()];
  return reduceToSingleDigit(day + month + (year % 100) + weekdayNum);
};

const calculateAntardashaTimeline = (
  day: number,
  month: number,
  startYear: number
) => {
  const sequence: Antardasha[] = [];

  for (let i = 0; i < 9; i += 1) {
    const year = startYear + i;
    const startDate = new Date(year, month - 1, day);
    const endDate = addDays(new Date(year + 1, month - 1, day), -1);

    sequence.push({
      number: getAntardashaNumberForYear(day, month, year),
      sequence: i + 1,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      durationDays: Math.round((addDays(endDate, 1).getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)),
    });
  }

  return sequence;
};

const calculatePratyantarDashaTimeline = (
  startNumber: number,
  startDateInput: Date
) => {
  const sequence: PratyantarDasha[] = [];
  let currentNumber = startNumber;
  let startDate = new Date(startDateInput);

  for (let i = 0; i < 9; i += 1) {
    const durationDays = pratyantarDurations[currentNumber] + (i === 8 ? 5 : 0);
    const endDate = addDays(startDate, durationDays);

    sequence.push({
      number: currentNumber,
      sequence: i + 1,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      durationDays,
    });

    startDate = endDate;
    currentNumber = nextNumber(currentNumber);
  }

  return sequence;
};

export const calculateVedicNumerology = (
  dobString: string,
  options: { targetDate?: string | Date } = {}
): VedicNumerologyResult => {
  const { dob, year, month, day } = parseDob(dobString);
  const targetDate = options.targetDate ? new Date(options.targetDate) : new Date();
  const targetYear = Number.isNaN(targetDate.getTime())
    ? new Date().getFullYear()
    : targetDate.getFullYear();

  const ruling = reduceToSingleDigit(day);
  const destiny = reduceToSingleDigit(day + month + year);
  const yearWithoutCentury = String(year).padStart(4, '0').slice(2);
  const dobDigits = `${day}${month}${yearWithoutCentury}`
    .split('')
    .map(Number)
    .filter((digit) => digit >= 1 && digit <= 9);
  const digits = dobDigits;

  const grid = [3, 1, 9, 6, 7, 5, 2, 8, 4].reduce<Record<number, number>>((acc, num) => {
    acc[num] = digits.filter((digit) => digit === num).length;
    return acc;
  }, {});

  const mahadashas: Mahadasha[] = [];
  let currentAge = 0;
  let currentNumber = ruling;

  while (currentAge < 100) {
    const endAge = Math.min(currentAge + currentNumber, 100);
    mahadashas.push({
      number: currentNumber,
      startAge: currentAge,
      endAge,
    });
    currentAge = endAge;
    currentNumber = nextNumber(currentNumber);
  }

  const birthdayThisYear = new Date(targetYear, dob.getMonth(), dob.getDate());
  const yearSum = reduceToSingleDigit(day + month + (targetYear % 100));
  const weekdayNum = weekdayMap[birthdayThisYear.getDay()];
  const antardasha = getAntardashaNumberForYear(day, month, targetYear);
  const antardashas = calculateAntardashaTimeline(day, month, targetYear);
  const analysis = getAnalysis(grid);

  return {
    dob: dobString,
    ruling,
    destiny,
    grid,
    mahadashas,
    antardasha,
    currentAntardasha: antardasha,
    antardashas,
    pratyantarDashas: calculatePratyantarDashaTimeline(antardasha, birthdayThisYear),
    ...analysis,
    meta: {
      targetYear,
      birthdayThisYear: toDateString(birthdayThisYear),
      yearSum,
      weekdayNum,
      gridLayout: [
        [3, 1, 9],
        [6, 7, 5],
        [2, 8, 4],
      ],
    },
  };
};
